import React, { useCallback, useMemo, useRef, useState } from "react";

import { useDeviceTier } from "../../hooks/useDeviceTier";
import { BottomSheet } from "./BottomSheet";
import "./MobileSelect.css";

/** Matches the sheet's close transition; see BottomSheet.css. */
const CLOSE_ANIMATION_MS = 360;

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Sheet heading, and the accessible name of the control. */
  label: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  /** Shown when the value matches no option. */
  placeholder?: string;
};

/**
 * A select that becomes a bottom sheet on touch tiers (phone and tablet).
 *
 * A native `<select>` hands its dropdown to the platform, which on a touch
 * device renders it outside the page — it can extend past the edge of the screen,
 * and its options take the OS's own colours rather than the app's (light
 * rows on a dark UI). Below the lg tier this presents the same choices as a
 * sheet: on-brand, always inside the viewport, and with rows big enough to
 * hit.
 *
 * At the desktop tier it renders an ordinary `<select>`, so desktop keeps the
 * behaviour and keyboard handling people expect.
 */
export const MobileSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  label,
  className,
  id,
  disabled,
  placeholder,
}) => {
  // Touch tiers get the sheet; only the desktop tier keeps the native control.
  // Tablets are touch devices too, and a native dropdown there is drawn by the
  // platform outside the page just as it is on a phone.
  const usesSheet = useDeviceTier() !== "desktop";
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /* The sheet moves focus into itself on open (see BottomSheet), so on close
     it has to be put back deliberately — otherwise focus falls to <body> and
     a keyboard user loses their place in the form.
     
     The restore has to wait for the sheet to finish closing. When this select
     lives inside another sheet (a picker within the Filters sheet), the outer
     sheet's subtree is still marked aria-hidden for a few frames after the
     inner one closes; focusing the trigger during that window puts focus in a
     hidden subtree and the browser blocks it. So we poll for the trigger to be
     reachable rather than guessing a delay. */
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (next) return;

    let frames = 0;
    const restore = () => {
      const el = triggerRef.current;
      if (!el) return;
      if (!el.closest('[aria-hidden="true"]')) {
        el.focus();
        return;
      }
      // ~20 frames is far longer than the close transition; bail rather than
      // spin if something keeps the attribute set.
      if (frames++ < 20) requestAnimationFrame(restore);
    };
    // After the close animation, so the library's own focus handling has
    // already run — restoring on the next frame would be immediately undone.
    window.setTimeout(restore, CLOSE_ANIMATION_MS);
  }, []);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const triggerText = selected?.label ?? placeholder ?? "Select…";

  if (!usesSheet) {
    return (
      <select
        id={id}
        className={className ?? "eventos-select"}
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={`mobile-select__trigger ${className ?? ""}`.trim()}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span className="mobile-select__value">{triggerText}</span>
        <span className="mobile-select__caret" aria-hidden>
          ▾
        </span>
      </button>

      <BottomSheet open={open} onOpenChange={handleOpenChange} title={label}>
        <div className="sheet-options" role="listbox" aria-label={label}>
          {options.map((o) => {
            const isSelected = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={o.disabled}
                className={`sheet-option${isSelected ? " is-selected" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  handleOpenChange(false);
                }}
              >
                <span className="sheet-option__label">{o.label}</span>
                {isSelected ? (
                  <span className="sheet-option__check" aria-hidden>
                    ✓
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
};
